import { styled } from 'styled-components';

/**
 * PageContainer - Edge-to-edge layout for iOS Safari
 * Uses position: fixed to fill the viewport, with padding for safe areas
 */
export const PageContainer = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    
    background: #F8F7F5;
    perspective: 1200px;
    perspective-origin: center center;
    
    /* Safe area padding pushes content inward */
    padding-top: env(safe-area-inset-top, 0px);
    padding-bottom: env(safe-area-inset-bottom, 0px);
    padding-left: env(safe-area-inset-left, 0px);
    padding-right: env(safe-area-inset-right, 0px);
    
    display: flex;
    align-items: center;
    justify-content: center;
    
    overflow: hidden;
    touch-action: none;
    overscroll-behavior: none;

    &[data-texture='on']::before {
        content: '';
        position: absolute;
        inset: 0;
        pointer-events: none;
        opacity: 0.35;
        mix-blend-mode: multiply;
        background-image:
            radial-gradient(rgba(0, 0, 0, 0.03) 1px, transparent 1px),
            radial-gradient(rgba(0, 0, 0, 0.02) 1px, transparent 1px);
        background-position: 0 0, 1px 1px;
        background-size: 4px 4px, 4px 4px;
    }

    &[data-modal-open='true'] {
        overflow: hidden;
    }
`;

/**
 * ContentContainer - 9:16 aspect ratio container for items
 * Centered within PageContainer
 */
export const ContentContainer = styled.div`
    position: relative;
    height: 100%;
    max-height: 100%;
    aspect-ratio: 9 / 16;
    width: auto;
    max-width: 100%;
    margin: 0;
    padding: 0;
    flex-shrink: 0;
    overflow: visible;
`;

export const TextureToggle = styled.button`
    position: absolute;
    top: 16px;
    right: 16px;
    z-index: 100;
    padding: 8px 16px;
    background: rgba(255, 255, 255, 0.9);
    border: 1px solid rgba(0, 0, 0, 0.1);
    border-radius: 4px;
    cursor: pointer;
    font-size: 14px;
    font-family: inherit;
    transition: background 150ms ease;

    &:hover {
        background: rgba(255, 255, 255, 1);
    }
`;

/**
 * ShadowBackground - Edge-to-edge shadow image
 * Uses negative margins to extend INTO safe area padding
 */
export const ShadowBackground = styled.img`
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    
    /* Negative margins pull image into safe area padding zones */
    margin-top: calc(-1 * env(safe-area-inset-top, 0px));
    margin-left: calc(-1 * env(safe-area-inset-left, 0px));
    margin-right: calc(-1 * env(safe-area-inset-right, 0px));
    margin-bottom: calc(-1 * env(safe-area-inset-bottom, 0px));
    
    width: 100vw;
    height: 100vh;
    object-fit: cover;
    z-index: -1;
    pointer-events: none;

    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: none;
`;

export const BackgroundSVG = styled.img`
    position: absolute;
    top: 48%;
    left: 50%;
    transform: translate3d(-50%, -50%, 0);
    width: 55%;
    /* Explicit aspect-ratio prevents layout shift while image loads */
    aspect-ratio: 216 / 109;
    z-index: 0;
    pointer-events: none;
    opacity: 1;
    transition: opacity 220ms ease-in-out;

    @media (max-width: 768px) {
        top: 46.5%;
    }

    /* Ensure crisp rendering on high-DPI displays */
    image-rendering: -webkit-optimize-contrast;
    -ms-interpolation-mode: bicubic;
    
    /* Ensure proper scaling */
    object-fit: contain;
    object-position: center;
    
    /* Hint browser about transforms for better rendering */
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;

    user-select: none;
    -webkit-user-select: none;
    -webkit-touch-callout: none;
    -webkit-tap-highlight-color: transparent;
    touch-action: none;

    &[data-faded='true'] {
        opacity: 0;
    }
`;

export const ScheduleTakeover = styled.div<{ $open: boolean; $isMobile: boolean }>`
    position: fixed;
    inset: 0;
    z-index: 1001;
    display: grid;
    place-items: center;
    background: rgba(0, 0, 0, 0.35);
    pointer-events: ${p => (p.$open ? 'auto' : 'none')};
    opacity: ${p => (p.$open ? 1 : 0)};
    transform: scale(${p => (p.$open ? 1 : 0.98)});
    transition: opacity 280ms ease, transform 280ms ease;

    > img {
        width: ${p => (p.$isMobile ? '100vw' : 'min(100vw, 100vh * 0.462)')};
        height: ${p => (p.$isMobile ? '100vh' : 'min(100vh, 100vw / 0.462)')};
        object-fit: contain;
        transform: rotate(0deg);
        box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
        background: #fff;
        border-radius: 8px;
    }
`;

export const InlineContentLayer = styled.div`
    position: absolute;
    inset: 0;
    z-index: 200;
    display: grid;
    place-items: center;
    pointer-events: auto;

    > * {
        pointer-events: auto;
    }
`;

export const InlineModalContainer = styled.div<{ $isMobile: boolean, $shadow: boolean }>`
    width: ${p => (p.$isMobile ? 'calc(100vw - 16px)' : 'calc(100% - 48px)')};
    height: ${p => (p.$isMobile ? '100%' : '100%')};
    
    overflow-x: visible;
    overflow-y: auto;

    background: transparent;
    will-change: opacity;
    box-shadow: ${p => (p.$shadow ? '0 24px 60px rgba(0, 0, 0, 0.35)' : 'none')};
    display: flex;
    flex-direction: column;
`;
